import React, { useState, useEffect, useMemo } from 'react';
import './App.css';

const COLOR_COLUMNS = ['black', 'gray', 'blue', 'purple', 'green', 'brown', 'yellow', 'orange', 'red', 'pink', 'white'];
const LOCAL_STORAGE_KEY = 'character_gallery_characters_txt';
const RESERVED_METADATA_KEYS = new Set(['asset', 'series']);
const GENERIC_COLUMN_LABELS = new Set([
  'all', 'anime', 'cartoon', 'cartoons', 'character', 'characters', 'favorite', 'favorites',
  'film', 'films', 'game', 'games', 'manga', 'misc', 'mixed', 'movie', 'movies', 'other',
  'television', 'tv',
]);
const COLLECTION_WORDS = new Set(['favorite', 'favorites', 'misc', 'mixed', 'other']);

function getStoredCharactersText() {
  try {
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return typeof stored === 'string' ? stored : null;
  } catch {
    return null;
  }
}

function setStoredCharactersText(text) {
  window.localStorage.setItem(LOCAL_STORAGE_KEY, text);
}

async function getDefaultCharactersText() {
  const res = await fetch('/characters.txt');
  if (!res.ok) throw new Error(`Could not load characters.txt (${res.status})`);
  return res.text();
}

function normalizeColor(value) {
  const normalized = (value || '').trim().toLowerCase();
  return COLOR_COLUMNS.includes(normalized) ? normalized : 'gray';
}

function normalizeFacetKey(value) {
  return (value || '').trim().toLowerCase();
}

function normalizeFacetValue(value) {
  return (value || '').trim().toLowerCase();
}

function parseTaggedFacetLine(line) {
  const trimmed = (line || '').trim();
  if (!trimmed.startsWith(':')) return null;

  const body = trimmed.slice(1).trim();
  const separatorIndex = body.indexOf(':');
  if (separatorIndex <= 0) return null;

  const key = normalizeFacetKey(body.slice(0, separatorIndex));
  const value = body.slice(separatorIndex + 1).trim();
  if (!key || !value) return null;

  return { key, value };
}

/**
 * Parses characters.txt into an array of columns.
 * Each block separated by a blank line becomes one column.
 * The first non-empty line is the column name.
 * Under each character, first plain color line is treated as color.
 * Additional metadata lines can be user-defined as :key:value pairs.
 */
export function parseCharactersTxt(text) {
  const blocks = text.trim().split(/\n[ \t]*\n/);
  return blocks
    .map(block => {
      const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length < 1) return null;
      const [name, ...rawEntries] = lines;
      const entries = [];

      let i = 0;
      while (i < rawEntries.length) {
        const characterName = rawEntries[i];
        i += 1;
        if (!characterName) continue;

        const facets = {};
        const maybeColor = rawEntries[i];

        if (COLOR_COLUMNS.includes((maybeColor || '').toLowerCase())) {
          facets.color = normalizeColor(maybeColor);
          i += 1;
        }

        while (i < rawEntries.length) {
          const taggedFacet = parseTaggedFacetLine(rawEntries[i]);
          if (!taggedFacet) break;

          facets[taggedFacet.key] = taggedFacet.key === 'color'
            ? normalizeColor(taggedFacet.value)
            : RESERVED_METADATA_KEYS.has(taggedFacet.key)
              ? taggedFacet.value
              : normalizeFacetValue(taggedFacet.value);
          i += 1;
        }

        entries.push({
          name: characterName,
          facets: {
            color: facets.color || 'gray',
            ...facets,
          },
          category: name,
        });
      }

      return { name, entries };
    })
    .filter(Boolean);
}

/**
 * Fetches the first scraped image result from the backend.
 * Reserved metadata can supply an exact asset or series. Generic grouping
 * columns are omitted from the query so labels such as "Games" do not get
 * mistaken for a franchise.
 */
export async function fetchScrapedImage(columnName, entry, useColumnAsSeries) {
  const characterName = typeof entry === 'string' ? entry : entry?.name;
  const explicitSeries = typeof entry === 'string' ? null : entry?.facets?.series;
  const manualAsset = typeof entry === 'string' ? null : entry?.facets?.asset;
  const series = explicitSeries || (useColumnAsSeries ? columnName : null);
  const query = [series, characterName, 'fanart']
    .filter(Boolean)
    .join(' ');

  try {
    const params = new URLSearchParams({ q: query });
    const res = await fetch(`/api/image-search?${params}`);
    const payload = await res.json().catch(() => ({}));

    if (!res.ok) {
      if (manualAsset) return { imageUrl: manualAsset, candidates: [manualAsset], error: null };
      return { imageUrl: null, candidates: [], error: payload.error || 'Search failed' };
    }

    const searchedCandidates = Array.isArray(payload.candidates) ? payload.candidates : [];
    const candidates = [...new Set([manualAsset, ...searchedCandidates].filter(Boolean))];
    return {
      imageUrl: candidates[0] ?? null,
      candidates,
      error: null,
    };
  } catch {
    if (manualAsset) return { imageUrl: manualAsset, candidates: [manualAsset], error: null };
    return { imageUrl: null, candidates: [], error: 'Could not reach image search API' };
  }
}

function getParentheticalHint(name) {
  return (name || '').match(/\(([^()]+)\)\s*$/)?.[1]?.trim().toLowerCase() || null;
}

export function shouldUseColumnAsSeries(column) {
  const normalizedName = (column.name || '').trim().toLowerCase();
  const words = normalizedName.match(/[a-z0-9]+/g) || [];
  if (GENERIC_COLUMN_LABELS.has(normalizedName)) return false;
  if (words.some(word => COLLECTION_WORDS.has(word))) return false;

  const seriesHints = new Set(
    column.entries
      .map(entry => entry.facets?.series?.toLowerCase() || getParentheticalHint(entry.name))
      .filter(Boolean)
  );
  return seriesHints.size <= 1;
}

/** A single data cell that fetches and toggles its image on click. */
function CharacterCell({ columnName, entry, useColumnAsSeries }) {
  // undefined = not yet fetched, null = fetched but no result, string = image URL
  const [image, setImage] = useState(undefined);
  const [candidates, setCandidates] = useState([]);
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  const handleClick = async () => {
    const next = !open;
    setOpen(next);
    if (next && image === undefined) {
      setLoading(true);
      const result = await fetchScrapedImage(columnName, entry, useColumnAsSeries);
      setImage(result.imageUrl);
      setCandidates(result.candidates);
      setCandidateIndex(0);
      setLookupError(result.error);
      setLoading(false);
    }
  };

  const handleNextImage = (event) => {
    event.stopPropagation();
    if (!candidates.length) return;

    const next = (candidateIndex + 1) % candidates.length;
    setCandidateIndex(next);
    setImage(candidates[next]);
    setLookupError(null);
  };

  const handleImageError = () => {
    const next = candidateIndex + 1;
    if (next < candidates.length) {
      setCandidateIndex(next);
      setImage(candidates[next]);
    } else {
      setImage(null);
      setLookupError('Search results could not be loaded');
    }
  };

  return (
    <td
      className={`char-cell${open ? ' open' : ''}`}
      onClick={handleClick}
      title={open ? 'Click to collapse' : 'Click to reveal image'}
    >
      <span className="char-name">{entry.name}</span>
      <span className="char-color">{entry.facets.color}</span>
      {open && (
        <div className="char-image">
          {loading ? (
            <div className="loading-text">Loading…</div>
          ) : image ? (
            <>
              <img src={image} alt={`${columnName} - ${entry.name}`} onError={handleImageError} />
              {candidates.length > 1 && (
                <button type="button" className="next-image-btn" onClick={handleNextImage}>
                  Wrong image? Try next ({candidateIndex + 1}/{candidates.length})
                </button>
              )}
            </>
          ) : (
            <div className="no-image">{lookupError || 'No image found'}</div>
          )}
        </div>
      )}
    </td>
  );
}

export default function App() {
  const [activeTab, setActiveTab] = useState('gallery');
  const [columns, setColumns] = useState([]);
  const [rawText, setRawText] = useState('');
  const [editorText, setEditorText] = useState('');
  const [error, setError] = useState(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle');

  const loadCharacters = async () => {
    setLoading(true);
    setError(null);

    try {
      const stored = getStoredCharactersText();
      const text = stored !== null ? stored : await getDefaultCharactersText();

      setRawText(text);
      setEditorText(text);
      setColumns(parseCharactersTxt(text));
      setLoading(false);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCharacters();
  }, []);

  const handleSave = async () => {
    setSaveStatus('saving');
    setSaveErrorMessage('');
    try {
      setStoredCharactersText(editorText);

      setRawText(editorText);
      setColumns(parseCharactersTxt(editorText));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1200);
    } catch (e) {
      setSaveErrorMessage(e?.message || 'Save failed.');
      setSaveStatus('error');
    }
  };

  const hasUnsavedChanges = editorText !== rawText;

  const handleDownload = () => {
    const blob = new Blob([editorText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'characters.txt';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const maxRows = useMemo(
    () => Math.max(...columns.map(c => c.entries.length), 0),
    [columns]
  );

  const allEntries = useMemo(
    () => columns.flatMap(column => column.entries.map(entry => ({ ...entry, category: column.name }))),
    [columns]
  );

  const facetTabs = useMemo(() => {
    const allFacetKeys = Array.from(new Set(allEntries.flatMap(entry => Object.keys(entry.facets || {}))));
    const dynamicFacetKeys = allFacetKeys
      .filter(key => key !== 'color' && !RESERVED_METADATA_KEYS.has(key))
      .sort((a, b) => a.localeCompare(b));
    return ['color', ...dynamicFacetKeys];
  }, [allEntries]);

  const activeFacet = activeTab.startsWith('facet:') ? activeTab.replace('facet:', '') : null;

  const facetColumns = useMemo(() => {
    if (!activeFacet) return [];
    if (activeFacet === 'color') return COLOR_COLUMNS;

    return Array.from(
      new Set(
        allEntries
          .map(entry => entry.facets?.[activeFacet])
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [activeFacet, allEntries]);

  const groupedFacetEntries = useMemo(
    () => facetColumns.reduce((acc, facetValue) => {
      acc[facetValue] = allEntries.filter(entry => (entry.facets?.[activeFacet] || '') === facetValue);
      return acc;
    }, {}),
    [facetColumns, allEntries, activeFacet]
  );

  const facetRows = useMemo(
    () => Math.max(...facetColumns.map(facetValue => groupedFacetEntries[facetValue].length), 0),
    [facetColumns, groupedFacetEntries]
  );

  useEffect(() => {
    if (!activeFacet) return;
    if (!facetTabs.includes(activeFacet)) setActiveTab('gallery');
  }, [activeFacet, facetTabs]);

  if (loading) return <div className="status">Loading…</div>;
  if (error)   return <div className="status error">{error}</div>;

  return (
    <div className="app">
      <h1>Character Gallery</h1>
      <p className="hint">
        This website is desisgned to help you display and categorize your favorite characters from your favorite series. With an infinite amount of customization options.
        <br />
        The format goes as follows :
        <br />
        - Each column is separated by a blank line. The first line of each column is the column name.
        <br />
        - Under each column, list character names from that series. 
        <br />
        - Under each character, the first plain color line is treated as the character's color.
        <br />
        - Additional metadata can be added as :key:value pairs under each character (:personality:stern).
        <br />
        - Use :series:name to clarify mixed collections, or :asset:url to provide a preferred image. These reserved tags do not create tabs.
        <br />
      </p>

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn${activeTab === 'gallery' ? ' active' : ''}`}
          onClick={() => setActiveTab('gallery')}
        >
          Gallery
        </button>
        {facetTabs.map(facet => (
          <button
            key={facet}
            type="button"
            className={`tab-btn${activeTab === `facet:${facet}` ? ' active' : ''}`}
            onClick={() => setActiveTab(`facet:${facet}`)}
          >
            {facet === 'color' ? 'Color Sort' : facet[0].toUpperCase() + facet.slice(1)}
          </button>
        ))}
        <button
          type="button"
          className={`tab-btn${activeTab === 'editor' ? ' active' : ''}`}
          onClick={() => setActiveTab('editor')}
        >
          Edit chars.txt
        </button>
      </div>

      {activeTab === 'gallery' ? (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col.name}>{col.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxRows }, (_, i) => (
                <tr key={i}>
                  {columns.map(col =>
                    col.entries[i] ? (
                      <CharacterCell
                        key={col.name}
                        columnName={col.name}
                        entry={col.entries[i]}
                        useColumnAsSeries={shouldUseColumnAsSeries(col)}
                      />
                    ) : (
                      <td key={col.name} className="empty-cell" />
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeFacet ? (
        <div className="table-wrapper color-table-wrap">
          <table className="color-table facet-table">
            <thead>
              <tr>
                {facetColumns.map(facetValue => (
                  <th
                    key={facetValue}
                    className={`color-col-head${activeFacet === 'color' ? ` color-${facetValue}` : ''}`}
                  >
                    {facetValue}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: facetRows }, (_, rowIndex) => (
                <tr key={rowIndex}>
                  {facetColumns.map(facetValue => {
                    const item = groupedFacetEntries[facetValue][rowIndex];
                    return (
                      <td
                        key={`${facetValue}-${rowIndex}`}
                        className={`color-cell${activeFacet === 'color' ? ` color-${facetValue}` : ''}`}
                      >
                        {item ? (
                          <>
                            <span className="color-name">{item.name}</span>
                            <span className="color-category">{item.category}</span>
                          </>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="editor-panel">
          <textarea
            value={editorText}
            onChange={e => setEditorText(e.target.value)}
            spellCheck={false}
            aria-label="Edit characters text"
          />
          <div className="editor-actions">
            <button type="button" onClick={handleSave} disabled={!hasUnsavedChanges || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : 'Save chars.txt'}
            </button>
            <button type="button" onClick={loadCharacters}>
              Reload saved copy
            </button>
            <button type="button" onClick={handleDownload}>
              Download characters.txt
            </button>
            {saveStatus === 'saved' && <span className="save-ok">Saved.</span>}
            {saveStatus === 'error' && <span className="save-error">{saveErrorMessage || 'Save failed.'}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
